import React, { useCallback, useEffect, useState } from 'react';
import { Ticket, Plus, Loader2, Ban } from 'lucide-react';
import { adminGetPromoCodes, adminCreatePromoCode, adminDeactivatePromoCode } from '../lib/api';

const fieldCls = 'bg-[#0b0b0b] border border-[#c6a15b]/30 px-3 py-2.5 font-body text-sm text-[#f7f3ec] outline-none focus:border-[#c6a15b]';

const codeStatus = (pc) => {
  if (!pc.active) return { label: 'Inactive', cls: 'bg-[#2a2620] text-[#9c9587] border-[#c6a15b]/20' };
  if (pc.expires_at && pc.expires_at * 1000 < Date.now()) return { label: 'Expired', cls: 'bg-[#3a2222] text-[#e0a6a6] border-[#8c4a4a]/50' };
  return { label: 'Active', cls: 'bg-[#1f3a28] text-[#8fe0a6] border-[#4a8c5b]/50' };
};

const fmtExpiry = (ts) => {
  if (!ts) return 'No expiry';
  try { return new Date(ts * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return '—'; }
};

const PromoManager = ({ flash }) => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ code: '', percent: 10, expires: '' });

  const load = useCallback(async () => {
    try {
      const res = await adminGetPromoCodes();
      setCodes(res.items || []);
    } catch (e) { flash?.(e.message); }
    setLoading(false);
  }, [flash]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    const code = form.code.trim().toUpperCase();
    if (!code || !/^[A-Z0-9]+$/.test(code)) { flash?.('Code must be letters and numbers only'); return; }
    const pct = Number(form.percent);
    if (!(pct > 0 && pct <= 100)) { flash?.('Discount must be between 1% and 100%'); return; }
    setCreating(true);
    try {
      const expiresIso = form.expires ? new Date(`${form.expires}T23:59:59`).toISOString() : null;
      const created = await adminCreatePromoCode(code, pct, expiresIso);
      setCodes((prev) => [created, ...prev]);
      setForm({ code: '', percent: 10, expires: '' });
      flash?.(`Promo code ${created.code} is live`);
    } catch (e) { flash?.(e.message); }
    setCreating(false);
  };

  const deactivate = async (pc) => {
    if (!window.confirm(`Deactivate ${pc.code}? Shoppers will no longer be able to use it.`)) return;
    setBusyId(pc.id);
    try {
      const updated = await adminDeactivatePromoCode(pc.id);
      setCodes((prev) => prev.map((c) => (c.id === pc.id ? updated : c)));
      flash?.(`${pc.code} deactivated`);
    } catch (e) { flash?.(e.message); }
    setBusyId('');
  };

  return (
    <div className="bg-[#141311] border border-[#c6a15b]/20 p-6 mb-10" data-testid="promo-manager">
      <div className="flex items-center gap-2 mb-2">
        <Ticket size={18} className="text-[#c6a15b]" />
        <h2 className="font-display text-2xl text-[#f7f3ec]">Promo Codes</h2>
      </div>
      <p className="font-body text-xs text-[#9c9587] mb-5">
        Create discount codes shoppers enter at checkout. Codes go live in Stripe instantly. Deactivate any code to retire it.
      </p>

      {/* Create form */}
      <div className="grid grid-cols-1 sm:grid-cols-[1.4fr_1fr_1.2fr_auto] gap-2 items-end mb-6">
        <label className="flex flex-col">
          <span className="overline text-[0.5rem] text-[#7a7365] mb-1">Code</span>
          <input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
            placeholder="SUMMER20" data-testid="promo-code-input" className={fieldCls} />
        </label>
        <label className="flex flex-col">
          <span className="overline text-[0.5rem] text-[#7a7365] mb-1">Discount %</span>
          <input type="number" min="1" max="100" value={form.percent}
            onChange={(e) => setForm((f) => ({ ...f, percent: e.target.value }))}
            data-testid="promo-percent-input" className={fieldCls} />
        </label>
        <label className="flex flex-col">
          <span className="overline text-[0.5rem] text-[#7a7365] mb-1">Expires (optional)</span>
          <input type="date" value={form.expires}
            onChange={(e) => setForm((f) => ({ ...f, expires: e.target.value }))}
            data-testid="promo-expires-input" className={fieldCls} />
        </label>
        <button onClick={create} disabled={creating} data-testid="promo-create-btn"
          className="btn-gold flex items-center justify-center gap-2 px-6 py-2.5 disabled:opacity-60">
          <span className="flex items-center gap-2">{creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Create</span>
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div className="py-8 flex justify-center"><Loader2 size={22} className="text-[#c6a15b] animate-spin" /></div>
      ) : codes.length === 0 ? (
        <p className="font-body text-sm text-[#7a7365] italic" data-testid="promo-empty">No promo codes yet.</p>
      ) : (
        <div className="space-y-2" data-testid="promo-list">
          {codes.map((pc) => {
            const st = codeStatus(pc);
            const canDeactivate = pc.active;
            return (
              <div key={pc.id} className="flex flex-wrap items-center gap-3 bg-[#0b0b0b]/40 border border-[#c6a15b]/10 px-4 py-3" data-testid={`promo-row-${pc.code}`}>
                <span className="font-display text-lg text-[#f7f3ec] tracking-wide">{pc.code}</span>
                <span className="font-body text-sm text-[#e7d3a1]">
                  {pc.percent_off != null ? `${pc.percent_off}% off` : pc.amount_off != null ? `$${(pc.amount_off / 100).toFixed(2)} off` : '—'}
                </span>
                <span className={`overline text-[0.5rem] px-2 py-1 border ${st.cls}`} data-testid={`promo-status-${pc.code}`}>{st.label}</span>
                <span className="font-body text-xs text-[#9c9587]">Expires: {fmtExpiry(pc.expires_at)}</span>
                <span className="font-body text-xs text-[#7a7365]">Used {pc.times_redeemed || 0}×</span>
                {canDeactivate && (
                  <button onClick={() => deactivate(pc)} disabled={busyId === pc.id}
                    data-testid={`promo-deactivate-${pc.code}`}
                    className="ml-auto flex items-center gap-2 px-3 py-2 border border-[#8c4a4a]/50 text-[#e0a6a6] overline text-[0.55rem] hover:bg-[#3a2222] transition-colors disabled:opacity-50">
                    {busyId === pc.id ? <Loader2 size={13} className="animate-spin" /> : <Ban size={13} />} Deactivate
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PromoManager;
